(ns frontend.view.memo.pdf-memo
  (:require
   [clojure.set :as set]
   [common.entity :as entity]
   [common.localization :as loc]
   [common.schema.registry :as registry]
   [common.svg.pdf-memo-svg :as pdf-memo-svg]
   [common.sync-progress :as sync-progress]
   [common.time :as c-time]
   [common.util :as cu]
   [frontend.db :as app-db]
   [frontend.logging :as logging]
   [frontend.routing :as routing]
   [frontend.state.current-session :as session]
   [frontend.state.permissions :as state-permissions]
   [frontend.utils :as fu]
   [frontend.view.entity-viewers.entities-editor :as entities-editor]
   [frontend.view.not-found :as not-found]
   [frontend.view.syncing-data :as syncing]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.components.radix-ui.dropdown-menu.view :as dropdown-menu]
   [ui.components.toggle.view :as toggle]))

(def hard-coded-templates
  {:entity.type/oppo {:left-hand-fields  [:oppo/status
                                          :oppo/funding-round-v2
                                          [:oppo/target-asset :asset/name]
                                          :oppo/owner
                                          :oppo/stage
                                          :oppo/lead-source-v2
                                          :oppo/cur-ticket
                                          :oppo/cur-ticket--sym]
                      :right-hand-fields [:oppo/case-note
                                          :oppo/status-and-next-steps
                                          [:oppo/target-asset :asset/description]
                                          [:oppo/target-asset :company/exit-plan]]}})


(defn read-access?
  [attr-def entity asset-entity]
  (let [[attribute entity-type] (cond (keyword? attr-def)
                                      [attr-def (:entity/type entity)]
                                      (coll? attr-def)
                                      [(last attr-def) (:entity/type asset-entity)])]
    (when (some #{attribute} (:tenant/fields-in-use @session/tenant))
      (state-permissions/field-read-access? attribute
                                            entity-type
                                            (fu/pipeline-id entity)))))

(defn short-fields-editor
  [state entity-cursor asset-cursor]
  (let [tenant-schema @session/tenant-schema
        entity @entity-cursor
        entity-id (:db/id entity)
        module-id :pdf-editor-short-fields
        target-asset-id (:db/id @asset-cursor)
        view-id (str "pdf-editor-left" entity-id)
        ordered-visible-fields (->> [(:entity/type entity) :left-hand-fields]
                                    (get-in hard-coded-templates)
                                    (filter (fn [a]
                                              (read-access? a @entity-cursor @asset-cursor))))
        all-entity-fields (->> entity
                               :entity/type
                               (registry/get-schema tenant-schema)
                               (map key)
                               (set))
        hidden-fields (set/difference all-entity-fields (set ordered-visible-fields))]
    [entities-editor/view
     {:field-schema-lookup         (fn [eid attr]
                                     (registry/get-field-schema tenant-schema attr))
      :drag-options                {:draggable? false}
      :state-atom                  state
      :class-name                  "text"
      :entity-ids                  [entity-id target-asset-id]
      :view-id                     view-id
      :module-id                   module-id
      :show-empty?                 (:show-blanks? @state)
      :override-show-empty-fields? true
      :override-editor-types       (->> ordered-visible-fields
                                        (map (fn [k]
                                               [k
                                                (->> k
                                                     (registry/get-field-schema tenant-schema)
                                                     :field/default-visibility
                                                     :grid)]))
                                        (into {}))
      :read-only-fields            (->> ordered-visible-fields
                                        (filter (fn [k]
                                                  (->> k
                                                       (registry/get-field-schema tenant-schema)
                                                       :field/default-visibility
                                                       :editor
                                                       (= :editor.type/none))))
                                        (set))
      :ordering                    (->> ordered-visible-fields
                                        (mapv (fn [attr]
                                                (if (keyword? attr)
                                                  [entity-id attr]
                                                  [target-asset-id (last attr)]))))
      :remove-fields               (merge
                                    {entity-id hidden-fields}
                                    (let [target-asset (session/get-entity target-asset-id)
                                          entity-fields (->> target-asset
                                                             :entity/type
                                                             (registry/get-schema tenant-schema)
                                                             (map key)
                                                             (set))]
                                      {target-asset-id (->> (set/difference entity-fields
                                                                            (->> ordered-visible-fields
                                                                                 (filter coll?)
                                                                                 (map last)
                                                                                 (set)))
                                                            (into []))}))}]))

(defn long-fields-editor
  [state entity-cursor asset-cursor]
  (let [tenant @session/tenant
        tenant-schema @session/tenant-schema
        entity-id (:entity-id @state)
        entity (get @session/entities entity-id)
        module-id :pdf-editor-long-fields
        target-asset-id (get entity :oppo/target-asset)
        view-id (str "pdf-editor-right" entity-id)
        ordered-visible-fields (->> [(:entity/type entity) :right-hand-fields]
                                    (get-in hard-coded-templates)
                                    (filter (fn [a]
                                              (read-access? a @entity-cursor @asset-cursor))))
        all-entity-fields (->> entity
                               :entity/type
                               (registry/get-schema tenant-schema)
                               (map key)
                               (set))
        hidden-fields (set/difference all-entity-fields (set ordered-visible-fields))]
    [entities-editor/view
     {:drag-options                {:draggable?     false
                                    :on-drag-start! nil
                                    :on-drag-end!   nil
                                    :on-drag-leave  nil
                                    :on-drag-enter  nil}
      :state-atom                  state
      :entity-ids                  [entity-id target-asset-id]
      :view-id                     view-id
      :class-name                  "mb-4"
      :module-id                   module-id
      :override-editor-types       (->> ordered-visible-fields
                                        (map (fn [k]
                                               [k :editor.type/textarea]))
                                        (into {}))
      :ordering                    (->> ordered-visible-fields
                                        (mapv (fn [attr]
                                                (if (keyword? attr)
                                                  [entity-id attr]
                                                  [target-asset-id (last attr)]))))
      :show-empty?                 (:show-blanks? @state)
      :override-show-empty-fields? true
      :read-only-fields            (->> ordered-visible-fields
                                        (filter (fn [k]
                                                  (->> k
                                                       (registry/get-field-schema tenant-schema)
                                                       :field/default-visibility
                                                       :editor
                                                       (= :editor.type/none))))
                                        (set))
      :remove-fields               (merge
                                    {entity-id hidden-fields}
                                    (let [target-asset (session/get-entity target-asset-id)
                                          entity-fields (->> target-asset
                                                             :entity/type
                                                             (registry/get-schema tenant-schema)
                                                             (map key)
                                                             (set))]
                                      {target-asset-id (->> (set/difference entity-fields
                                                                            (->> ordered-visible-fields
                                                                                 (filter coll?)
                                                                                 (map last)
                                                                                 (set)))
                                                            (into []))}))}]))


;(defn show-as-pdf! [state-atom local-atom entity]
;      (let [doc-definition (template->pdfmake-content state-atom entity)
;            pdfDoc (.call (aget js/pdfMake "createPdf") js/pdfMake (clj->js doc-definition))]
;           (.call (aget pdfDoc "getDataUrl") pdfDoc
;                  (fn [outdoc]
;                      (aset (.getElementById js/document "pdf-frame") "src" outdoc)))))


(defn get-text-value
  [tenant-schema tenant attribute entity]
  (let [field-schema (registry/get-field-schema tenant-schema attribute)]
    (fu/display-data tenant-schema tenant attribute field-schema entity :show-currency? true)))


;(entity/display-string (session/get-entity (:oppo/owner entity)



(defn template->pdfmake-content
  [tenant state entity asset-entity value-font]
  (let [entity-fields (get hard-coded-templates (:entity/type entity))
        tenant-schema @session/tenant-schema
        {:keys [page-size font-size-kw show-blanks?]} @state]
    {:info         {:title (loc/tr :memo-name [(entity/display-string entity)])}
     :defaultStyle {:font "NotoSans"}
     :pageSize     (case page-size
                     :page.size/A4 "A4"
                     :page.size/letter "LETTER"
                     :page.size/legal "LEGAL"
                     "A4")
     :header       (fn [currentPage pageCount]
                     (clj->js {:table  {:widths ["*"]
                                        :body   [[{:svg       pdf-memo-svg/header-svg
                                                   :width     220
                                                   :alignment "right"}]
                                                 #_[{:text ""}
                                                    {:svg    :logo                                  ;; TODO replace with SVG or image on the header
                                                     :width  150
                                                     :margin [-20 0 0 0]}]]}
                               :layout "noBorders"}))
     :footer       (fn [currentPage pageCount]
                     (clj->js {:table  {:widths [220 "*" 220]
                                        :body   [[{:text      ""
                                                   :alignment "left"}
                                                  {:text      ""                                    ;(str " - " currentPage " - ")
                                                   :alignment "center"}
                                                  {:svg       pdf-memo-svg/logo-svg
                                                   :width     150
                                                   :alignment "right"}
                                                  ]]}
                               :margin [10 0 10 0]
                               :layout "noBorders"}))
     :content
     [{:table
       {:widths ["30%" "*"]
        :body
        [[{:table
           {:widths ["*"]
            :body   (->> entity-fields
                         :left-hand-fields
                         (remove (fn [a]
                                   (let [attr (if (coll? a) (last a) a)]
                                     (->> attr
                                          (registry/get-field-schema tenant-schema)
                                          :field/secondary-field?))))
                         (mapcat (fn [a]
                                   (let [text-value (get-text-value tenant-schema tenant a entity)]
                                     (when (and (read-access? a entity asset-entity)
                                                (or show-blanks?
                                                    (not (cu/blank-value? text-value))))
                                       [[[{:text      (let [attr (if (seq? a)
                                                                   (last a)
                                                                   a)]
                                                        (let [fs (registry/get-field-schema
                                                                  tenant-schema
                                                                  attr)]
                                                          (loc/field-name attr fs)))
                                           :fillColor "#CCCCCC"
                                           :style     :left-hand-label}]]
                                        [[{:text        text-value
                                           :fillColor   "#CCCCCC"
                                           :border      [true true true true]
                                           :borderColor ["#DDD" "#DDD" "#DDD" "#DDD"]
                                           :style       :left-hand-value}]]]))))
                         (vec))}
           :layout "noBorders"}
          {:table  {:widths ["*"]
                    :body   (let [rows (->> entity-fields
                                            :right-hand-fields
                                            (mapcat
                                             (fn [a]
                                               (let [text-value (get-text-value tenant-schema
                                                                                tenant
                                                                                a
                                                                                entity)]
                                                 (when (and (read-access? a entity asset-entity)
                                                            (or show-blanks?
                                                                (not (cu/blank-value? text-value))))
                                                   [[[{:text  (let [attr (if (seq? a)
                                                                           (last a)
                                                                           a)]
                                                                (let
                                                                 [fs (registry/get-field-schema
                                                                      tenant-schema
                                                                      attr)]
                                                                  (loc/field-name attr fs)))
                                                       :style :right-hand-label}]]
                                                    [[{:text  text-value
                                                       :style :right-hand-value}]]]))))
                                            (vec))]
                              (if (empty? rows)
                                [[]]
                                rows))}
           :layout "noBorders"}]]}
       :layout "noBorders"}]
     :images       {:logo "/img/zapflow-logo_color-RGB.svg"}
     :styles       {:main-header      {:fontSize 12
                                       :bold     true
                                       :margin   [0 0 0 20]}
                    :subheader        {:fontSize 15
                                       :bold     true}
                    :table-header     {:fontSize 12
                                       :bold     true}
                    :quote            {:italics true}
                    :preface          {:fontSize  8
                                       :alignment "justify"
                                       :color     "#666666"
                                       :margin    [0 0 0 4]}
                    :left-hand-label  {:fontSize (case font-size-kw
                                                   :lg 12
                                                   :md 10
                                                   :sm 8
                                                   8)
                                       :bold     false
                                       :margin   [0 4 0 0]
                                       :color    "#999999"}
                    :left-hand-value  {:fontSize (case font-size-kw
                                                   :lg 14
                                                   :md 12
                                                   :sm 10
                                                   10)
                                       :bold     false
                                       :font     value-font
                                       :margin   [0 0 4 4]
                                       :color    "#333333"}
                    :right-hand-label {:fontSize (case font-size-kw
                                                   :lg 12
                                                   :md 10
                                                   :sm 8
                                                   8)
                                       :bold     false
                                       :margin   [0 4 0 0]
                                       :color    "#999999"}
                    :right-hand-value {:fontSize (case font-size-kw
                                                   :lg 14
                                                   :md 12
                                                   :sm 10
                                                   10)
                                       :bold     false
                                       :font     value-font
                                       :margin   [0 0 4 4]
                                       :color    "#333333"}
                    :link             {:fontSize   11
                                       :bold       false
                                       :margin     [4 4 4 4]
                                       :decoration "underline"
                                       :color      "blue"}
                    :file             {:fontSize 11
                                       :bold     false
                                       :margin   [4 4 4 4]
                                       :color    "#333333"}
                    :input-line       {:fontSize   11
                                       :bold       false
                                       :margin     [4 4 4 4]
                                       :decoration "underline"
                                       :color      "#333333"}}}))

(def value-font "NotoSans")                                                                         ;; TBD - paid service + dropdown for selecting font to be used...

(defn customizable-fonts
  []
  (let [now (c-time/now-millis)]
    {"NotoSans"  {:normal      (str "https://static.zapflow.com/fonts/NotoSans-Regular.ttf")
                  :bold        (str "https://static.zapflow.com/fonts/NotoSans-Bold.ttf")
                  :italics     (str "https://static.zapflow.com/fonts/NotoSans-Italic.ttf")
                  :bolditalics (str "https://static.zapflow.com/fonts/NotoSans-BoldItalic.ttf")}
     "Roboto"    {:normal      (str "Roboto-Regular.ttf")
                  :bold        (str "Roboto-Regular.ttf")
                  :italics     (str "Roboto-Regular.ttf")
                  :bolditalics (str "Roboto-Regular.ttf")}
     ;; Chinese
     ;; NOTE The fonts can be REALLY big - this one is 20MB
     #_"XiaolaiSC" #_{:normal      (str "https://static.zapflow.com/fonts/XiaolaiSC-Regular.ttf#v=" now)
                  :bold        (str "https://static.zapflow.com/fonts/XiaolaiSC-Regular.ttf#v=" now)
                  :italics     (str "https://static.zapflow.com/fonts/XiaolaiSC-Regular.ttf#v=" now)
                  :bolditalics (str "https://static.zapflow.com/fonts/XiaolaiSC-Regular.ttf#v=" now)
                  }}))

(defn used-fonts
  [value-font]
  (concat ["NotoSans"]
          (when (and value-font
                     (not= value-font "NotoSans"))
            [value-font])))

(defn show-pdf
  [tenant state entity asset-entity]
  (let [doc-definition (template->pdfmake-content tenant state entity asset-entity value-font)
        pdfDoc (.call (aget js/pdfMake "createPdf")
                      js/pdfMake
                      (clj->js doc-definition)
                      nil
                      (clj->js (select-keys (customizable-fonts) (used-fonts value-font))))]
    (.call (aget pdfDoc "getDataUrl")
           pdfDoc
           (fn [outdoc]
             (aset (.getElementById js/document "pdf-frame") "src" outdoc)))))


(defn download-pdf
  [tenant state entity asset-entity]
  (try
    (js/console.log "Download")
    (let [doc-definition (template->pdfmake-content tenant state entity asset-entity value-font)
          _ (js/console.log "Download doc " doc-definition)
          pdfDoc (.call (aget js/pdfMake "createPdf")
                        js/pdfMake
                        (clj->js doc-definition)
                        nil
                        (clj->js (select-keys (customizable-fonts) (used-fonts value-font))))]
      (js/console.log "Download pdf doc " pdfDoc)
      (.call (aget pdfDoc "download")
             pdfDoc
             (str (->> doc-definition
                       :info
                       :title)
                  ".pdf")))
    (catch js/Object o
      (logging/error o "HERE"))))

(defn preview
  []
  [:iframe
   {:id     "pdf-frame"
    :height (- (:height @app-db/window) 32)
    :width  (- (/ (:width @app-db/window) 2) 32)}])

(defn trigger-preview
  [{:keys [preview-state view-state entity-cursor asset-cursor]}]
  (let [asset-trigger @asset-cursor
        entity-trigger @entity-cursor
        view-trigger @view-state
        preview-timeout (:preview-timeout @preview-state)]
    (when preview-timeout
      (js/clearTimeout preview-timeout))
    (swap! preview-state assoc
           :preview-timeout
           (js/setTimeout (fn []
                            (show-pdf @session/tenant view-state @entity-cursor @asset-cursor))
                          1000))))

(defn size-dropdown
  [state]
  [dropdown-menu/view
   {:trigger [dropdown-menu/button-trigger-open
              "Page size"
              [:i.fas.fa-angle-down]]
    :items   [{:label      (loc/tr :page.size/A4)
               :disabled?  false
               :on-click   (fn [_]
                             (swap! state assoc :page-size :page.size/A4))
               :icon-class ""}
              {:label      (loc/tr :page.size/legal)
               :disabled?  false
               :on-click   (fn [_]
                             (swap! state assoc :page-size :page.size/legal))
               :icon-class ""}
              {:label      (loc/tr :page.size/letter)
               :disabled?  false
               :on-click   (fn [_]
                             (swap! state assoc :page-size :page.size/letter))
               :icon-class ""}]}])

(defn font-size
  [state]
  (let [{:keys [page-size show-blanks? font-size-kw]} @state]
    [:ul.font-toggle-list
     (->> [:lg :md :sm]
          (map (fn [size]
                 ^{:key (str "pdf-memo-font-size-" (name size))}
                 [:li
                  {:class-name (fu/cs "font-toggle"
                                      (str "font-toggle--" (name size))
                                      (when (= size font-size-kw)
                                        "active"))
                   :on-click   (fn [_]
                                 (swap! state assoc :font-size-kw size))}
                  [:i.far.fa-font]])))]))

(defn toolbar
  [state entity-cursor asset-cursor]
  (let [{:keys [page-size show-blanks? font-size-kw]} @state]
    [:div
     {:class-name "w-full py-3 flex gap-3 justify-between items-center bg-complement-light"}
     [button/secondary-open
      {:on-click (fn [e]
                   (.preventDefault e)
                   (routing/open-entity (:entity-id @state)))}
      [:i.fal.fa-chevron-left]
      (loc/tr :exit)]
     [:div
      {:class-name "hidden md:flex"}
      [font-size state]]
     [:div
      {:class-name "hidden md:flex gap-3 items-center"}
      [:label.input-editor__label
       (loc/tr :memo-page-size [(loc/tr page-size)])]
      [size-dropdown state]]
     [:div
      {:class-name "flex gap-4 items-center"}
      [:span
       {:class-name "hidden md:flex gap-3 items-center"}
       [:label.input-editor__label (loc/tr :show-blank-fields)]
       [toggle/toggle
        {:checked?   (boolean show-blanks?)
         :on-change (fn []
                      (swap! state update :show-blanks? not))}]]
      [button/sm-secondary-hollow
       {:on-click (fn [e]
                    (js/console.log "Click " e)
                    (.preventDefault e)
                    (let [resp (download-pdf @session/tenant state @entity-cursor @asset-cursor)]
                      (js/console.log "Downloaded : " resp)))}
       (loc/tr :download)]]]))

(defn pdf-editor-page
  [{:keys [view-state entity-cursor asset-cursor]}]
  (let [preview-state (atom {})]
    (r/create-class
     {:component-did-mount    (fn []
                                (swap! view-state assoc
                                       :tracker
                                       (r/track!
                                        (partial trigger-preview
                                                 {:preview-state preview-state
                                                  :view-state    view-state
                                                  :asset-cursor  asset-cursor
                                                  :entity-cursor entity-cursor}))))
      :component-will-unmount (fn []
                                (r/dispose! (:tracker @view-state)))
      :reagent-render
      (fn [{:keys [view-state entity-cursor asset-cursor]}]
        (let [entity @entity-cursor
              target-asset @asset-cursor]
          [:div.zap-page.bg-white
           [:div
            [:div
             {:class-name "grid grid-cols-10"}
             [:div
              {:class-name "col-span-10 lg:col-span-5"}
              [:div
               {:class-name "flex flex-col h-screen"}
               [:div
                {:class-name "px-4 flex text-base-regular-text bg-base-regular"}
                [toolbar view-state entity-cursor asset-cursor]]
               [:div
                {:class-name "flex-1 overflow-auto"}
                [:div
                 {:class-name "md:px-4 grid grid-cols-10 gap-6"}
                 [:div
                  {:class-name "col-span-10 md:col-span-10 lg:col-span-4"}
                  [:div
                   [short-fields-editor view-state entity-cursor asset-cursor]]]
                 [:div
                  {:class-name "col-span-10 md:col-span-10 lg:col-span-6"}
                  [long-fields-editor view-state entity-cursor asset-cursor]]]]]]
             [:div
              {:class-name "col-span-10 lg:col-span-5 hidden lg:flex justify-center pt-3 bg-base-regular"}
              [preview]]]]]))})))

(defn pdf-memo-view
  [entity-id]
  (let [view-state (r/atom {:entity-id    entity-id
                            :show-blanks? false
                            :page-size    :page.size/A4
                            :font-size-kw :md})
        entity-cursor (r/cursor session/entities [entity-id])
        asset-cursor (r/cursor session/entities [(:oppo/target-asset @entity-cursor)])]
    (fn [entity-id]
      (cond
        (not (session/has-feature-access? :feature/pdf-memo))
        [not-found/not-found-view]

        (not (= :entity.type/oppo (:entity/type @entity-cursor)))
        [not-found/not-found-view]

        :else
        [pdf-editor-page
         {:view-state    view-state
          :entity-cursor entity-cursor
          :asset-cursor  asset-cursor}]))))

(defmethod routing/render-view :pdf-memo
  [{:keys [route-params]}]
  [syncing/wait-until-sync-progress sync-progress/COMPLETE
   (let [entity-id (-> route-params
                       :id
                       (js/parseInt))]
     ^{:key (str entity-id)}                                                                        ;; <- remove if remount is not needed for entity-id changes
     [pdf-memo-view entity-id])])
